import { Store } from 'vuex';
import { AppState } from '@/store';
import { Logger } from '../logger/logger';
import { PromiseCacher } from '@/helpers/async';
import { MenuService } from '../menu/menu.service';
import { MENU_METADATA_CHANNELS, MetadataService } from '../metadata/metadata.service';
import {
  MonoMenu,
  MonoMenuTopLevelGroup,
  MonoMenuIdentifiers,
  MonoMenuCacheObject,
  MonoMenuEntree,
  MonoMenuItem,
  MenuItem,
  MenuEntree,
  MonoMenuEntreeGroup,
  MonoMenuCacheItem,
  MonoMenuEntreeRules,
  MonoMenuTopLevelNutritionDetailsSection,
  MonoMenuNutritionDetail,
  MenuItemGroup,
  DietaryTagUI,
  IngredientGroupMetadata,
  RecipeGroup,
  MonoMenuTileItem,
} from '@/models';
import { isDefined, isDesktop } from '@/helpers/util';
import { awaitCondition } from '@/helpers/async';

import {
  entreeMenuPassthrough,
  entreeRulesMenuPassthrough,
  entreeMMDPassthrough,
  itemMenuPassthrough,
  itemRulesMenuPassthrough,
  itemMMDPassthrough,
  itemRulesMMDPassthrough,
  utilityItems,
  utilityEntrees,
  linkedEntreeTypeOptionTacos,
  linkedEntreeTypeOptionKids,
  selectNoneMap,
  EntreeCustomizationId,
  utilityItemKeys,
  topLevelMenuMapDuplications,
  requiredNutritionFields,
  excludedItemTypes,
  allowedNoNutritionItemTypes,
  staticUtilityItems
} from './mono-menu-constants';

import { MUTATIONS as MONO_MENU_MUTATIONS } from '@/store/mono-menu';
import { LoyaltyService } from '../loyalty/loyalty.service';
import { addIncludedItemGroup, removeCountrySpecificItems, removeExcludedContentGroups, removeExcludedUniversalItems } from './mono-menu-rules';
import { isMenuItemRewardsExclusive } from '@/helpers';

export interface MonoMenuService {
  awaitCacheItems(): Promise<boolean>;
  awaitCacheItemsAndBuildMenu(): Promise<boolean>;
  getTopLevelMenuByTitle(title: string, nutrition?: Boolean): MonoMenuTopLevelGroup;
  getTopLevelMenu(nutrition: Boolean): MonoMenuTopLevelGroup[];
  getTopLevelMenuNutritionDetailSections(): any[];
  getMonoMenuEntree(entreeId: string, nutrition?: Boolean): MonoMenuEntree | undefined;
  getMonoMenuItem(itemId: string, nutrition?: Boolean, asEntreeId?: string): MonoMenuItem | undefined;
  getMonoMenuEntreeHalfPortion(entreeId: string, nutrition?: Boolean): string;
  getMonoMenuEntreeExtraPortion(entreeId: string, nutrition?: Boolean): string;
  getNutritionDetailSections(): MonoMenuTopLevelNutritionDetailsSection[];
  getNutritionDetailSection(sectionKey: string): MonoMenuNutritionDetail;
  getDietaryTagsLookup(): { [key: string]: DietaryTagUI };
  isItemInMenu(itemId: string): boolean;
  setMetadataCache(): Promise<void>;
  setMonoMenuResizeListener(listner: () => void): void;
  isEntreeId(itemId: string | undefined): boolean;
  buildRecipeGroups(ingredientGroups: IngredientGroupMetadata[]): void;
  getItemGroupTitle(entree: MonoMenuEntree, itemId: string): string;
  isDefaultItem(item: MonoMenuTileItem): Boolean | undefined;
  isItemAvailable(item: MonoMenuTileItem): boolean;
}

export class AppMonoMenuService implements MonoMenuService {
  constructor(
    private store: Store<AppState>,
    private menu: MenuService,
    private metadataService: MetadataService,
    private loyalty: LoyaltyService,
    private logger: Logger,
    private promiseCacher: PromiseCacher,
  ) {}

  monoMenuCache: { [index: string]: MonoMenu } = {};
  isRefreshing: { [index: string]: boolean } = {};

  async awaitCacheItems(): Promise<boolean> {
    // a lighter version of awaitCacheItemsAndBuildMenu for fast UI events like resolution change
    const cacheKey = this.getCurrentCacheKey(this.getCurrentMonoMenuIdentifiers(true));
    if (!!this.store.state.monoMenu.monoMenuCache[cacheKey]) {
      return true;
    }
    return this.awaitCacheItemsAndBuildMenu();
  }

  async awaitCacheItemsAndBuildMenu(): Promise<boolean> {
    const cacheKey = this.getCurrentCacheKey(this.getCurrentMonoMenuIdentifiers(true));
    if (this.isRefreshing[cacheKey]) {
      await awaitCondition(() => !!this.store.state.monoMenu.monoMenuCache[cacheKey]);
      return true;
    }

    this.isRefreshing[cacheKey] = true;
    const menuStore = this.store.state.menu;
    // if we have a resturantID the menu load should already be happening
    if (!this.restaurantID && !menuStore.universalMenuFull) {
      this.menu.refreshUniversalMenuFull(true);
    }

    // await metadata
    await awaitCondition(() => !!this.store.state.ui.clientWidth);
    const currentMMDCacheName = `${isDesktop(this.store.state) ? MENU_METADATA_CHANNELS.DESKTOP : MENU_METADATA_CHANNELS.MOBILE}-nutrition`;
    if (!this.store.state.menu.cachedMetadata[currentMMDCacheName].data) {
      this.setMetadataCache();
    }

    if (this.restaurantID) {
      await this.menu.waitForRestaurantMenuLoad();
    } else {
      await awaitCondition(() => !!menuStore.universalMenuFull && !!menuStore.universalMenuFull.menu);
    }

    await awaitCondition(() => !!this.store.state.menu.cachedMetadata[currentMMDCacheName].data);
    this.getOrCreateCachedMenu(true);

    await awaitCondition(() => !!this.store.state.monoMenu.monoMenuCache[cacheKey]);

    this.isRefreshing[cacheKey] = false;

    return true;
  }

  // - get top level menu by title
  getTopLevelMenuByTitle(title: string, nutrition: Boolean = true) {
    const { monoMenu } = this.getCachedMenu(nutrition);
    return monoMenu.topLevelMenu[monoMenu.topLevelMenuMap[title]];
  }

  // - get top level menu list
  getTopLevelMenu(nutrition: Boolean = true) {
    const { monoMenu } = this.getCachedMenu(nutrition);
    return monoMenu.topLevelMenu;
  }

  // - get top level menu nutrition detail sections
  getTopLevelMenuNutritionDetailSections(): any[] {
    const { monoMenu } = this.getCachedMenu(true);
    return monoMenu.nutritionDetailSections;
  }

  // - get current mono-menu for entree, current restaurant (results will differ based on selected resturaunt, rewards status)
  getMonoMenuEntree(entreeId: string, nutrition: Boolean = true) {
    const { monoMenu } = this.getCachedMenu(nutrition);
    if (!monoMenu.entrees[entreeId]) {
      return undefined;
    }

    const isEnrolledInLoyalty = this.loyalty.isEnrolledInLoyalty(this.store.state.customer.profile);
    const entree = Object.assign({}, monoMenu.entrees[entreeId]);

    if (!isEnrolledInLoyalty) {
      // filter out the loyalty items
      entree.groups.forEach((group, groupIndex) => {
        group.items.forEach((itemId, itemsIndex) => {
          if (isMenuItemRewardsExclusive(monoMenu.items[itemId]?.tags) && isMenuItemRewardsExclusive(monoMenu.entrees[itemId]?.tags)) {
            group.items.splice(itemsIndex, 1);
          }
        });
        if (group.items.length === 0) {
          entree.groups.splice(groupIndex, 1);
        }
      });
    } // TEST THIS

    return entree;
  }

  // - get mono-menu item
  getMonoMenuItem(itemId: string, nutrition: Boolean = true, asEntreeId?: string): MonoMenuItem | undefined {
    const { monoMenu } = this.getCachedMenu(nutrition);

    const monoMenuCacheItem = monoMenu.items[itemId];
    if (!monoMenuCacheItem) {
      // This type cast is necessary due to type mismatches between
      // the interface and the class.
      // This is a short-term fix to preserve existing behavior and should
      // be updated down the road to properly describe the type relationship.
      return monoMenuCacheItem as unknown as MonoMenuItem;
    }

    // get the right version for the current entree or itemCategory if its a side or drink
    const currenEntreeType = !!monoMenuCacheItem.itemCategory
      ? monoMenuCacheItem.itemCategory
      : monoMenu.entrees[asEntreeId || this.store.state.mealBuilderV2.entreeId!]?.itemType;

    const customizations =
      monoMenuCacheItem.customizations[currenEntreeType] && monoMenuCacheItem.customizations[currenEntreeType]?.length !== 0
        ? [{ id: null, name: 'Normal', countTowardsCustomizationMax: 0, countTowardsContentMax: 0, countTowardsOnTheSideCustomizationMax: 0 }, ...(monoMenuCacheItem.customizations[currenEntreeType] || [])]
        : [];

    const monoMenuItem = Object.assign({}, monoMenu.items[itemId], {
      // add the default normal customization here so we dont have to duplicate it for every entree type
      customizations,
      rules: Object.assign({}, monoMenuCacheItem.rules, {
        contentGroupName: monoMenuCacheItem.rules.contentGroupName[currenEntreeType] || Object.keys(monoMenuCacheItem.rules.contentGroupName)[0] + 'Group',
        pricingReferenceItemId: monoMenuCacheItem.rules.pricingReferenceItemId[currenEntreeType],
      }),
    }) as MonoMenuItem;
    return monoMenuItem;
  }

  getMonoMenuEntreeHalfPortion(entreeId: string, nutrition: Boolean = true) {
    return this.getCachedMenu(nutrition).monoMenu.halfPortionMap[entreeId];
  }

  getMonoMenuEntreeExtraPortion(entreeId: string, nutrition: Boolean = true) {
    return this.getCachedMenu(nutrition).monoMenu.extraPortionMap[entreeId];
  }

  getNutritionDetailSections() {
    return this.getCachedMenu().monoMenu.nutritionDetailSections;
  }

  getNutritionDetailSection(sectionKey: string) {
    return this.getCachedMenu().monoMenu.nutritionDetailsMap[sectionKey];
  }

  getDietaryTagsLookup() {
    return this.getCachedMenu().monoMenu.dietaryTagsLookup;
  }

  isItemInMenu(itemId: string) {
    const menu = this.getCachedMenu().monoMenu;
    const allItemIds = Object.keys(menu.items).concat(Object.keys(menu.entrees));
    return allItemIds.includes(itemId);
  }

  // simple getters and helpers
  get mmdChannel(): string {
    return isDesktop(this.store.state) ? 'web' : 'web-mobile';
  }

  get restaurantID(): number {
    return this.store.state.restaurant.selectedRestaurant?.restaurantNumber || 0;
  }

  getCurrentMonoMenuIdentifiers(nutrition: Boolean = true): MonoMenuIdentifiers {
    return {
      restaurantID: this.restaurantID,
      mmdChannel: this.mmdChannel,
      nutrition,
    };
  }

  getCurrentCacheKey(mmi: MonoMenuIdentifiers): string {
    return `${mmi.restaurantID}-${mmi.mmdChannel}-nutrition:${mmi.nutrition}`;
  }

  getMenu(restaurantID: number) {
    if (restaurantID) {
      return this.store.state.menu.cachedMenus[restaurantID].menu;
    }
    return this.store.state.menu.universalMenuFull!.menu;
  }

  getCachedMenu(nutrition: Boolean = true) {
    const mmi = this.getCurrentMonoMenuIdentifiers(nutrition);
    const key = this.getCurrentCacheKey(mmi);
    if (this.store.state.monoMenu.monoMenuCache[key]) {
      return { monoMenu: this.store.state.monoMenu.monoMenuCache[key].menu, mmi };
    } else {
      throw new Error('Call not allowed until after monomenu build');
    }
  }

  // Builders cache management
  getOrCreateCachedMenu(nutrition: Boolean = true) {
    const mmi = this.getCurrentMonoMenuIdentifiers(nutrition);

    this.clearCacheIfExpired(mmi);
    const key = this.getCurrentCacheKey(mmi);
    if (this.store.state.monoMenu.monoMenuCache[key]) {
      return { monoMenu: this.store.state.monoMenu.monoMenuCache[key].menu, mmi };
    }
    const newMonoMenu = this.buildMonoMenu(mmi);

    this.store.commit(MONO_MENU_MUTATIONS.CACHE_MONO_MENU, newMonoMenu);
    return { monoMenu: newMonoMenu.menu, mmi };
  }

  clearCacheIfExpired(mmi: MonoMenuIdentifiers) {
    const cacheKey = this.getCurrentCacheKey(mmi);
    const monoMenu = this.store.state.monoMenu.monoMenuCache[cacheKey];

    if (!monoMenu) {
      return;
    }

    // compare for menu
    let menuEtag = '';
    if (mmi.restaurantID) {
      menuEtag = this.store.state.menu.cachedMenus[mmi.restaurantID]?.etag;
    } else {
      menuEtag = this.store.state.menu.universalMenu?.etag || '';
    }
    if (monoMenu?.menuEtag != menuEtag) {
      delete this.store.state.monoMenu.monoMenuCache[cacheKey];
      return;
    }

    // compare for metadata
    const mmdEtag = this.store.state.menu.cachedMetadata[mmi.mmdChannel].etag;
    if (monoMenu?.metaDataEtag != mmdEtag) {
      delete this.store.state.monoMenu.monoMenuCache[cacheKey];
    }
  }

  buildMonoMenu(mmi: MonoMenuIdentifiers): MonoMenuCacheObject {
    const menuStore = this.store.state.menu;

    const mmd = this.getMetadata(mmi.mmdChannel);
    if (mmd) {
      const topLevelMenu = this.buildTopLevelMenu(mmi);
      const topLevelMenuMap = topLevelMenu.reduce((map: { [index: string]: number }, topLevelGroup, idx) => {
        map[topLevelGroup.title] = idx;
        return map;
      }, {});

      topLevelMenuMapDuplications.forEach(e => {
        topLevelMenuMap[e[1]] = topLevelMenuMap[e[0]];
      });

      const { monoMenuEntrees, entreeContentsItems } = this.buildEntrees(mmi);
      const { monoMenuItems, halfPortionMap, extraPortionMap } = this.buildItems(mmi, entreeContentsItems);

      return {
        key: this.getCurrentCacheKey(mmi),
        menuEtag: mmi.restaurantID ? menuStore.cachedMenus[mmi.restaurantID].etag : menuStore.universalMenuFull?.etag || '',
        metaDataEtag: menuStore.cachedMetadata[mmi.mmdChannel].etag!,
        nutrition: mmi.nutrition,
        menu: {
          resturantId: mmi.restaurantID,
          topLevelMenu,
          topLevelMenuMap,
          entrees: monoMenuEntrees,
          items: monoMenuItems,
          halfPortionMap,
          extraPortionMap,
          nonFoodItems: this.getMenu(mmi.restaurantID).nonFoodItems || [],
          nutritionDetailSections: (mmd as any).nutritionDetailSections as MonoMenuTopLevelNutritionDetailsSection[],
          nutritionDetailsMap: this.buildNutritionDetailsMap(((mmd as any).nutritionDetailSections as any) as MonoMenuTopLevelNutritionDetailsSection[]),
          dietaryTagsLookup: mmd.dietaryTagsLookup,
        },
      };
    }

    return {
      key: this.getCurrentCacheKey(mmi),
      menuEtag: '',
      metaDataEtag: '',
      nutrition: false,
      menu: {
        resturantId: mmi.restaurantID,
        topLevelMenu: [],
        topLevelMenuMap: {},
        entrees: {},
        items: {},
        halfPortionMap: {},
        extraPortionMap: {},
        nonFoodItems: [],
        nutritionDetailSections: [],
        nutritionDetailsMap: {},
        dietaryTagsLookup: {},
      },
    };
  }

  buildTopLevelMenu(mmi: MonoMenuIdentifiers): MonoMenuTopLevelGroup[] {
    const mmd = this.getMetadata(mmi.mmdChannel);
    const menu = this.getMenu(mmi.restaurantID);
    if (!mmd || !menu) {
      return [];
    }

    const mmdGroupMap: {
      [index: string]: MenuItemGroup;
    } = {};
    mmd.groups.forEach(g => (mmdGroupMap[g.id] = g));

    const topLevelMenu = mmd.topLevelMenu!.groups.map(tlm => {
      const group = mmdGroupMap[tlm.menuItemGroupId];
      const monoTLM: MonoMenuTopLevelGroup = {
        type: group.type,
        title: group.displayName,
        tags: group.tags,
        thumbnailImageUrl: group.thumbnailImageUrl,
        bannerImageUrl: group.bannerImageUrl,
        description: group.description,
      };

      switch (
        group.displayName // special cases for groups
      ) {
        case 'Sides & Drinks':
          monoTLM.type = 'non-entree';
          monoTLM.defaultEntreeId = utilityItemKeys.sidesEntree;
          break;
        case 'Tacos':
          monoTLM.defaultEntreeId = 'CMG-201';
          break;
        case `Kid's Meal`:
          monoTLM.defaultEntreeId = 'CMG-3002';
          break;
        case 'Lifestyle Bowl':
          break;
        default:
          // make the first entree that actually exists in the menu the default (prevents a metadata-menu mismatch on the default entree)
          // if there are no entrees, the default entree id doesnt matter because it wont be an option in the NC entree selector
          monoTLM.defaultEntreeId = group.items.filter(groupItem => !!menu.entrees?.find(entree => entree.itemId === groupItem.menuItemId && this.store.state.menu.metadata?.items[groupItem.menuItemId]))[0]?.menuItemId || 'default';     
      }

      return monoTLM;
    });
    return topLevelMenu;
  }

  buildEntrees(mmi: MonoMenuIdentifiers): { monoMenuEntrees: { [index: string]: MonoMenuEntree }; entreeContentsItems: { [index: string]: MenuItem } } {
    const mmd = this.getMetadata(mmi.mmdChannel);
    const menu = this.getMenu(mmi.restaurantID);
    if (!mmd || !menu) {
      return { monoMenuEntrees: {}, entreeContentsItems: {} };
    }

    const monoMenuEntrees: { [index: string]: MonoMenuEntree } = {};
    const entreeContentsItems: { [index: string]: MenuItem } = {};
    const allowedEntrees = new Set(menu.entrees?.map(i => i.itemId));
    const allowedSidesAndDrinks = (menu.sides || []).concat(menu.drinks || []).map(i => i.itemId);
    const linkedEntreeMap = this.buildEntreeLinksMap(menu.entrees || []);

    menu
      .entrees!.filter(menuEntree => !!mmd.items[menuEntree.itemId]) // Valid entrees need to exist in both MI and MMD
      .forEach(menuEntree => {
        menuEntree.contents!.forEach(item => {
          item.entreeItemType = menuEntree.itemType;
          entreeContentsItems[`${item.itemId}-${menuEntree.itemType}`] = item;
        });

        const mmdEntree = mmd.items[menuEntree.itemId]!;
        //TODO: This is an 'any' because TS freaks out at the number of possible types in the assignment below if the Mono Entree has a specific type
        const monoEntree: Partial<{ [key in keyof MonoMenuEntree]: any }> = {};

        entreeMenuPassthrough.forEach(i => (monoEntree[i] = menuEntree[i as keyof MenuEntree]));
        entreeMMDPassthrough.forEach(i => {
          if (mmdEntree[i as keyof typeof mmdEntree]) {
            monoEntree[i] = mmdEntree[i as keyof typeof mmdEntree];
          }
        });

        const rules: Partial<{ [key in keyof MonoMenuEntreeRules]: any }> = {};
        entreeRulesMenuPassthrough.forEach(i => (rules[i] = menuEntree[i as keyof MenuEntree]));
        monoEntree.rules = rules as MonoMenuEntreeRules;

        monoEntree.allowedItems = menuEntree.contents!.map(i => i.itemId);
        const allowedItems = new Set([...monoEntree.allowedItems, ...allowedSidesAndDrinks]);

        const mmdGroup =
          menuEntree.itemType !== 'Tacos'
            ? mmd.groups.find(g => g.menuItemType === menuEntree.itemType)!
            : mmd.groups.find(g => {
                // we have to find the right tacos group but there's no identifier other than the CMGIds about which group is which
                const idNumber = (cmgID: string) => parseInt(cmgID.slice(4, cmgID.length), 10);
                return (
                  g.menuItemType === menuEntree.itemType &&
                  g.items &&
                  ((idNumber(g.items[0].menuItemId) > 1000 && idNumber(menuEntree.itemId) > 1000) ||
                    (idNumber(g.items[0].menuItemId) < 1000 && idNumber(menuEntree.itemId) < 1000))
                );
              })!;
        const nonEntreeGroups: MonoMenuEntreeGroup[] = mmdGroup.itemSections.map(sectionKey => {
          const section = mmd.itemSections[sectionKey]!;
          const sectionItems = section.items.map(i => i.menuItemId).filter(id => allowedItems.has(id));
          // Items list is filtered because not all entrees of the same. mmd AND menu availability needs to be respected

          if (section.hasSelectNoneOption && selectNoneMap[section.name]) {
            entreeContentsItems[`${selectNoneMap[section.name].itemId}-${menuEntree.itemType}`] = {
              itemId: selectNoneMap[section.name].itemId,
              entreeItemType: menuEntree.itemType,
              contentGroupName: section.name,
            } as MenuItem;
            allowedItems.add(selectNoneMap[section.name].itemId);
            sectionItems.push(selectNoneMap[section.name].itemId);
          }

          const contentGroup = menuEntree.contentGroups?.find(cg => cg.contentGroupName === section.name);
          return {
            type: 'item',
            title: section.alias,
            subtitle: section.name === 'ToppingsContentGroup' ? `Choose up to ${contentGroup?.maxQuantity}` : '',
            contentGroupName: section.name,
            hasSelectNoneOption: section.hasSelectNoneOption || false,
            items: sectionItems,
            minQuantity: contentGroup?.minQuantity,
            maxQuantity: contentGroup?.maxQuantity,
          };
        });

        monoEntree.allowedItems = Array.from(allowedItems); // incase we added utility items
        monoEntree.groups = [];

        addIncludedItemGroup(monoEntree.itemType, monoEntree.groups);

        const entreeGroup = {
          type: 'entree',
          title: 'PROTEIN OR VEGGIE',
          subtitle: '',
          contentGroupName: 'EntreeContentGroup',
          items: mmdGroup.items.map(i => i.menuItemId).filter(id => allowedEntrees.has(id)),
        };

        switch(monoEntree.itemType) {
          case 'KidsBYO':
          case 'KidsQuesadilla':
            entreeGroup.subtitle = 'choose up to one';
            break;
          case 'Tacos':
            entreeGroup.subtitle = '';
            break;
          default:
            entreeGroup.subtitle = 'choose any two';
            break;
        }

        monoEntree.groups.push(entreeGroup);

        monoEntree.groups = monoEntree.groups.concat(nonEntreeGroups.filter(group => group.items.length > 0));
        monoEntree.groups = removeExcludedContentGroups(monoEntree.itemType, monoEntree.groups);

        monoEntree.customizations =
          menuEntree.maxExtras > 0
            ? [
                { id: null, name: 'Normal', countTowardsCustomizationMax: 0, countTowardsContentMax: 0, countTowardsOnTheSideCustomizationMax: 0 },
                {
                  id: EntreeCustomizationId.Double,
                  name: 'Double',
                  countTowardsCustomizationMax: 0,
                  countTowardsContentMax: 0,
                  countTowardsOnTheSideCustomizationMax: 0
                },
              ]
            : [];

        if (linkedEntreeMap[monoEntree.itemId]) {
          monoEntree.linkedEntreeId = linkedEntreeMap[monoEntree.itemId];
        }

        if (monoEntree.itemType === 'Tacos') {
          monoEntree.linkedEntreeTypeOptions = linkedEntreeTypeOptionTacos;
          monoEntree.entreeOptionTitle = parseInt(monoEntree.itemId.slice(4), 10) < 1000 ? 'Three Tacos' : 'One Taco'; // if the menu ever gets rewriten we really need proper identifiers
        }
        if (monoEntree.itemType === 'KidsBYO' || monoEntree.itemType === 'KidsQuesadilla') {
          monoEntree.linkedEntreeTypeOptions = linkedEntreeTypeOptionKids;
          monoEntree.entreeOptionTitle = monoEntree.itemType === 'KidsBYO' ? 'Kid’s Build Your Own' : 'Kid’s Quesadilla';
        }
        monoMenuEntrees[menuEntree.itemId] = monoEntree as MonoMenuEntree;
      });

    // build sides utility item
    const sidesGroup = mmd.groups.find(i => i.displayName === 'Sides & Drinks');
    if (sidesGroup) {
      const sidesId = utilityItemKeys.sidesEntree;
      monoMenuEntrees[sidesId] = utilityEntrees[sidesId];

      monoMenuEntrees[sidesId].allowedItems = (menu.sides || []).concat(menu.drinks || []).map(i => i.itemId);
      const allowedItems = new Set(monoMenuEntrees[sidesId].allowedItems);
      monoMenuEntrees[sidesId].groups = sidesGroup.itemSections
        .map(sectionKey => {
          const section = mmd.itemSections[sectionKey]!;
          return {
            type: 'item',
            title: section.alias,
            items: section.items.map(i => i.menuItemId).filter(id => allowedItems.has(id)), // Items list is filtered because not all entrees of the same. mmd AND menu availability needs to be respected
            contentGroupName: section.name,
            hasSelectNoneOption: section.hasSelectNoneOption || false,
          };
        })
        .filter(group => group.items.length !== 0);
    }

    return { monoMenuEntrees, entreeContentsItems };
  }

  buildEntreeLinksMap(entrees: readonly MenuEntree[]) {
    const linkedEntreeMap: { [index: string]: string | undefined } = {};
    entrees.forEach(e => {
      if (e.itemType === 'Tacos') {
        linkedEntreeMap[e.itemId] = entrees.find(
          entree => entree.itemType === 'Tacos' && entree.itemId !== e.itemId && entree.primaryFillingName === e.primaryFillingName,
        )?.itemId;
      }

      if (e.itemType === 'KidsBYO') {
        linkedEntreeMap[e.itemId] = 'CMG-3102';
      }

      if (e.itemType === 'KidsQuesadilla') {
        linkedEntreeMap[e.itemId] = 'CMG-3002';
      }
    });
    return linkedEntreeMap;
  }

  buildItems(
    mmi: MonoMenuIdentifiers,
    menuItemsMap: { [index: string]: MenuItem },
  ): { monoMenuItems: { [index: string]: MonoMenuCacheItem }; halfPortionMap: { [index: string]: string }; extraPortionMap: { [index: string]: string } } {
    const mmd = this.getMetadata(mmi.mmdChannel);
    const menu = this.getMenu(mmi.restaurantID);
    if (!mmd || !menu) {
      return { monoMenuItems: {}, halfPortionMap: {}, extraPortionMap: {} };
    }

    (menu.sides || []).forEach(item => {
      menuItemsMap[`${item.itemId}-${item.itemCategory}`] = item;
    });
    (menu.drinks || []).forEach(item => {
      menuItemsMap[`${item.itemId}-${item.itemCategory}`] = item;
    });

    const monoMenuItems = utilityItems; // start with the utility items
    // for each utility item, allow it to apply to every entree type so they will always show up in validation
    const allEntreeTypes = mmd.groups.map(group => group.menuItemType).filter(type => !!type) as string[];
    Object.keys(monoMenuItems).forEach(itemId => {
      allEntreeTypes.forEach(type => {
        monoMenuItems[itemId].rules.contentGroupName[type] = monoMenuItems[itemId].rules.contentGroupName['Burrito'];
      });
    });

    const halfPortionMap: { [index: string]: string } = {};
    const extraPortionMap: { [index: string]: string } = {};

    Object.keys(menuItemsMap).forEach(menuItemKey => {
      const menuItem = menuItemsMap[menuItemKey];
      const mmdItem = mmd.items[menuItem.itemId]!;

      if (menuItem.itemType === 'HalfPortion' && menuItem.pricingReferenceItemId) {
        halfPortionMap[menuItem.pricingReferenceItemId] = menuItem.itemId;
      }

      if (menuItem.itemType === 'ExtraPortion' && menuItem.pricingReferenceItemId) {
        extraPortionMap[menuItem.pricingReferenceItemId] = menuItem.itemId;
      }

      if (
        menuItem &&
        mmdItem &&
        (allowedNoNutritionItemTypes.includes(menuItem.itemType) || requiredNutritionFields.every((key: string) => Object.keys(mmdItem.nutrition).includes(key))) &&
        !excludedItemTypes.includes(menuItem.itemType)
      ) {
        if (monoMenuItems[menuItem.itemId] === undefined) {
          const monoMenuItem: any = {};

          itemMenuPassthrough.forEach(i => {
            monoMenuItem[i] = menuItem[i as keyof MenuItem];
          });
          monoMenuItem.customizations = { [menuItem.entreeItemType || menuItem.itemCategory]: menuItem.customizations?.sort((a, b) => a.id - b.id) };
          itemMMDPassthrough.forEach(i => {
            if (mmdItem[i as keyof typeof mmdItem]) {
              monoMenuItem[i] = mmdItem[i as keyof typeof mmdItem];
            }
          });

          monoMenuItem.rules = {};
          itemRulesMenuPassthrough.forEach(i => (monoMenuItem.rules[i] = menuItem[i as keyof MenuItem]));
          monoMenuItem.rules.contentGroupName = { [menuItem.entreeItemType || menuItem.itemCategory]: menuItem.contentGroupName };
          monoMenuItem.rules.pricingReferenceItemId = { [menuItem.entreeItemType || menuItem.itemCategory]: menuItem.pricingReferenceItemId };
          itemRulesMMDPassthrough.forEach(i => (monoMenuItem.rules[i] = mmdItem[i as keyof typeof mmdItem]));

          monoMenuItems[menuItem.itemId] = monoMenuItem;
        } else {
          monoMenuItems[menuItem.itemId].customizations[menuItem.entreeItemType || menuItem.itemCategory] = menuItem.customizations;
          monoMenuItems[menuItem.itemId].rules.contentGroupName[menuItem.entreeItemType || menuItem.itemCategory] = menuItem.contentGroupName;
          monoMenuItems[menuItem.itemId].rules.pricingReferenceItemId[menuItem.entreeItemType || menuItem.itemCategory] = menuItem.pricingReferenceItemId;
        }
      }
    });

    // Overwrite hardcoded data
    const overWriteProps = (['thumbnailUrl', 'nutrition', 'dietaryTags'] as any) as (keyof MonoMenuCacheItem)[];
    Object.keys(staticUtilityItems).forEach(itemId => {
      const monoMenuRefItem = monoMenuItems[monoMenuItems[itemId].itemId];

      overWriteProps.forEach(prop => {
        (monoMenuItems[itemId] as any)[prop] = monoMenuRefItem[prop];
      });
    });
    monoMenuItems['QUESADILLA_CHEESE_CMG-5252'].nutrition = Object.assign({}, monoMenuItems['QUESADILLA_CHEESE_CMG-5252'].nutrition);
    const quesadillaCheeseNutrition: { [index: string]: number } = monoMenuItems['QUESADILLA_CHEESE_CMG-5252'].nutrition as any;
    Object.keys(quesadillaCheeseNutrition).forEach(nutritionKey => (quesadillaCheeseNutrition[nutritionKey] *= 3));

    for (const [key, value] of Object.entries(selectNoneMap)) {
      const cacheItem = monoMenuItems[value.itemId];
      if (cacheItem.thumbnailUrl && cacheItem.thumbnailUrl.indexOf(this.store.state.cms.baseUrl) == -1) {
        monoMenuItems[value.itemId].thumbnailUrl = this.store.state.cms.baseUrl + cacheItem.thumbnailUrl;
      }
    }

    if (!this.restaurantID) {
      removeExcludedUniversalItems(monoMenuItems);
    }
    removeCountrySpecificItems(this.store.state.ui.countryProfile.countryCode, monoMenuItems);

    return { monoMenuItems, halfPortionMap, extraPortionMap };
  }

  buildNutritionDetailsMap(nutritionDetailSections: MonoMenuTopLevelNutritionDetailsSection[]): { [index: string]: MonoMenuNutritionDetail } {
    const map = {} as { [index: string]: MonoMenuNutritionDetail };
    const pushToMap = (section: MonoMenuNutritionDetail) => {
      if (section.key) {
        map[section.key] = {
          key: section.key,
          name: section.name,
          unit: section.unit,
          badgeColor: section.badgeColor,
        };
      }
    };

    nutritionDetailSections?.forEach(section => {
      pushToMap(section);
      if (section.components) {
        section.components.forEach(section => {
          pushToMap(section);
        });
      }
    });
    return map;
  }

  async setMetadataCache(): Promise<void> {
    const cachedMetadata = this.store.state.menu.cachedMetadata;

    const desktopMetadata = cachedMetadata[`${MENU_METADATA_CHANNELS.DESKTOP}-nutrition`];
    const mobileMetadata = cachedMetadata[`${MENU_METADATA_CHANNELS.MOBILE}-nutrition`];

    // Get cached metadata and etag based on metadata channel
    const metadata = isDesktop(this.store.state) ? desktopMetadata.data : mobileMetadata.data;
    const etag = isDesktop(this.store.state) ? desktopMetadata.etag : mobileMetadata.etag;
    if (!metadata) {
      await this.metadataService.refreshMenuMetadataWithNutrition(false);
    }
    this.getOrCreateCachedMenu(true);
  }

  getMetadata(channel: string) {
    return this.store.state.menu.cachedMetadata[`${channel}-nutrition`]?.data;
  }

  setMonoMenuResizeListener(listener: () => void) {
    this.store.commit(MONO_MENU_MUTATIONS.SET_RESIZE_LISTENER, listener);
  }

  isEntreeId(itemId: string | undefined): boolean {
    const { monoMenu } = this.getCachedMenu(true);
    if (itemId && !!monoMenu.entrees[itemId]?.itemId) {
      return true;
    }

    return false;
  }

  buildRecipeGroups(ingredientGroups: IngredientGroupMetadata[]): void {
    let recipeGroups: RecipeGroup[] = [];

    ingredientGroups.forEach(group => {
      let recipeGroup: RecipeGroup = { id: group.id, title: group.title, recipes: [] };

      group.items.forEach(groupItem => {
        let monoMenuItem = this.getMonoMenuEntree(groupItem.id, true) ?? this.getMonoMenuItem(groupItem.id, true, 'CMG-1');

        if (monoMenuItem) {
          recipeGroup.recipes.push({
            itemId: monoMenuItem.itemId,
            itemName: (monoMenuItem as MonoMenuEntree).primaryFillingName ?? monoMenuItem.itemName,
            thumbnailUrl: monoMenuItem.thumbnailUrl,
            ingredients: groupItem.ingredients,
            nutrition: monoMenuItem.nutrition,
          });
        }
      });

      recipeGroups.push(recipeGroup);
    });

    this.store.commit(MONO_MENU_MUTATIONS.SET_RECIPE_GROUPS, recipeGroups);
  }

  getItemGroupTitle(entree: MonoMenuEntree, itemId: string): string {
    for (let group of entree.groups) {
      if (group.items.find(i => i == itemId)) {
        return group.title;
      }
    }
    return '';
  }

  isDefaultItem(item: MonoMenuTileItem): Boolean | undefined {
    return (item as MonoMenuCacheItem).rules.defaultContent;
  }  
  
  isItemAvailable(item: MonoMenuTileItem): boolean {
    return isDefined(item) && !item.tags.find(tag => tag.value === 'nchide');
  }
}
